import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, CalendarCheck, ScanLine, XCircle, Lock, ArrowRight } from "lucide-react";

const bullets = [
  { Icon: ScanLine, text: "1 full body scan every month" },
  { Icon: CalendarCheck, text: "Founder rate starts October 30, 2026" },
  { Icon: XCircle, text: "Cancel anytime" },
  { Icon: ShieldCheck, text: "No payment until opening" },
];

export const Offer = ({ stats, onJoin }) => {
  const pct = Math.min(100, Math.round((stats.founding_count / stats.limit) * 100));

  return (
    <section id="offer" data-testid="offer-section" className="relative px-6 md:px-12 py-24 md:py-32 border-t border-white/10 overflow-hidden">
      <div className="pointer-events-none absolute top-1/3 -left-40 h-[30rem] w-[30rem] rounded-full bg-[radial-gradient(circle_at_center,rgba(30,111,255,0.18),transparent_65%)] blur-3xl" />
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center relative">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-xs uppercase tracking-[0.35em] text-volt mb-4">Founding 100 — members only</p>
          <h2 className="font-display uppercase leading-[0.9] tracking-tight" data-testid="offer-price">
            <span className="text-[clamp(4rem,12vw,8rem)]">$49</span>
            <span className="text-2xl md:text-3xl text-white/50 ml-2">/month</span>
          </h2>
          <p className="mt-2 text-white/40 line-through text-lg" data-testid="offer-price-old">$199 per scan</p>
          <p className="mt-4 font-display uppercase text-2xl md:text-3xl tracking-wide" data-testid="offer-zero-today"><span className="text-gold">$0 today</span> <span className="text-white/60 text-lg md:text-xl">— $49/month at opening</span></p>
          <div className="mt-6 inline-flex items-center gap-2 border border-gold/40 bg-gold-soft px-4 py-2 text-xs uppercase tracking-[0.2em] text-gold">
            <Lock className="h-3.5 w-3.5" /> $49/month, locked for life
          </div>
          <ul className="mt-10 space-y-4" data-testid="offer-bullets">
            {bullets.map(({ Icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-white/75">
                <Icon className="h-5 w-5 text-volt shrink-0" />
                <span className="text-sm md:text-base">{text}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="border border-white/10 bg-black/60 backdrop-blur-xl p-8 md:p-12"
          data-testid="founding-counter-card"
        >
          <p className="text-xs uppercase tracking-[0.35em] text-white/50 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-volt animate-pulse" /> Live counter
          </p>
          <div className="mt-6 flex items-end gap-2">
            <AnimatePresence mode="popLayout">
              <motion.span
                key={stats.founding_count}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="font-display text-7xl md:text-8xl leading-none text-volt"
                data-testid="counter-value"
              >
                {stats.founding_count}
              </motion.span>
            </AnimatePresence>
            <span className="font-display text-4xl md:text-5xl text-white/30 leading-none">/ 100</span>
          </div>
          <p className="mt-2 text-sm uppercase tracking-[0.25em] text-white/60">spots taken</p>
          <div className="mt-8 h-3 w-full bg-white/10 overflow-hidden" data-testid="counter-progress-track">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="h-full bg-volt shadow-[0_0_24px_rgba(30,111,255,0.8)]"
              data-testid="counter-progress-bar"
            />
          </div>
          <p className="mt-4 text-sm text-white/60" data-testid="counter-status">
            {stats.is_full
              ? "Waitlist full — join the reserve list"
              : `${stats.spots_left} of 100 founding spots left`}
          </p>
          <motion.button
            data-testid="offer-join-button"
            onClick={onJoin}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-volt px-8 py-4 font-display uppercase text-lg tracking-wide text-white shadow-[0_0_40px_rgba(30,111,255,0.45)] hover:bg-volt-dark transition-colors"
          >
            {stats.is_full ? "Join the reserve list" : "Reserve your spot — $0 today"}
            <ArrowRight className="h-5 w-5" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};
